import { API } from '@xbto/api-client';
import {
  action,
  Action,
  actionOn,
  ActionOn,
  computed,
  Computed,
  Thunk,
  thunk,
} from 'easy-peasy';
import { factories, getApiErrorMessage, runApi } from '../../utils';
import { DataModel } from '../data-store';
import { ApiThunk, Injections } from '../types';
import { BaseModel, createBaseModel } from '../base-store';
import { DEFAULTS } from '../../constants';
import { Enriched } from '../../types';
import { _extendPausableModel, _PausableModel } from '../_pausable/store';
import { processMarketsData, trackMarketsData } from './utils';

const defaultListAssetsRequest = {
  types: null,
  categories: null,
  sortDirection: null,
  sortColumn: null,
  search: null,
  pageSize: null,
  page: null,
};

type MarketData = {
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  result: API.ListAssetDetailsResponse[];
};

export type RemoveNullable<T> = {
  [K in keyof T]: NonNullable<T[K]>;
};

export type MarketFilters = RemoveNullable<API.MarketListAssetsRequest>;

export interface MarketsModel extends BaseModel, _PausableModel {
  _onViewChange: ActionOn<MarketsModel, DataModel>;

  _filters: MarketFilters;
  _setFilters: Action<MarketsModel, MarketFilters>;

  // >> `<api>/market/list`
  _data: MarketData | null;
  _setData: Action<MarketsModel, MarketData | null>;
  data: Computed<MarketsModel, Enriched.MarketData | null, DataModel>;
  getData: Thunk<
    MarketsModel,
    | {
        body?: API.MarketListAssetsRequest;
        reFetch?: boolean;
      }
    | undefined,
    Injections,
    DataModel
  >;
  fundCodes: string[] | null;
  setFundCodes: Action<MarketsModel, string[] | null>;
  funds: API.ListAssetDetailsResponse[] | null;
  setFunds: Action<MarketsModel, API.ListAssetDetailsResponse[] | null>;
  getFundCodes: ApiThunk<MarketsModel, void, string[]>;
  getFundDetails: ApiThunk<
    MarketsModel,
    string[],
    API.ListAssetDetailsResponse[]
  >;
  getFunds: Thunk<
    MarketsModel,
    void,
    Injections,
    DataModel,
    Promise<API.ListAssetDetailsResponse[]>
  >;
  startAggregatorTracking: Thunk<
    MarketsModel,
    undefined,
    Injections,
    DataModel
  >;
}

export const initialMarketFilters: MarketFilters = {
  page: 1,
  // Note: We agreed in product requirements that this would be a large number so for first implementation we do not show pagination control
  pageSize: DEFAULTS.PAGE_SIZE * 5, // default page size is 20, setting markets v2 page to 100
  search: '',
  types: [API.MarketAssetType.Crypto],
  categories: [] as API.MarketAssetCategory[],
  sortDirection: API.SortDirection.Descending,
  sortColumn: API.MarketListAssetsSortType.MarketCap,
};

export const marketsModel: MarketsModel = {
  ...createBaseModel(),
  ..._extendPausableModel(),

  _onViewChange: actionOn(
    (_actions, storeActions) => storeActions.setCurrentViewName,
    (state, target) => {
      state.paused =
        target.payload !== 'markets-list' && target.payload !== 'asset-details';
    }
  ),

  _filters: { ...initialMarketFilters },
  _setFilters: action((state, payload) => {
    state._filters = payload;
  }),

  _data: null,
  _setData: action((state, payload) => {
    state._data = payload;
  }),
  data: computed([s => s._data], _marketsData => {
    if (!_marketsData) {
      return null;
    }
    const _rows = factories.enrichListAssetDetails(_marketsData?.result || []);
    const result: Enriched.MarketData = {
      rows: _rows,
      pagination: _marketsData.pagination,
    };
    return result;
  }),
  getData: thunk(
    async (
      actions,
      _payload,
      { injections: { apiClient, marketsDataAggregator }, getState }
    ) => {
      try {
        if (!_payload?.reFetch) {
          return;
        }

        actions.setBusy(true);

        const body =
          _payload?.body || getState()._filters || initialMarketFilters;
        const listResponse = await apiClient.listAssets(body);

        if (listResponse.errorCode || !listResponse.isSuccessful) {
          actions.setError(listResponse.errorMessage);
          actions.setBusy(false);
          return;
        }

        const pagination = {
          page: listResponse?.page,
          pageSize: listResponse?.pageSize,
          totalCount: listResponse?.totalCount,
          totalPages: listResponse?.totalPages,
        };

        const { result, errorMessage } = await processMarketsData(
          listResponse.result,
          marketsDataAggregator,
          apiClient
        );

        if (errorMessage) {
          actions.setError(errorMessage);
          actions.setBusy(false);
          return;
        }

        actions._setData({
          result,
          pagination,
        });
        actions.setBusy(false);
      } catch (error) {
        const message = getApiErrorMessage(error);
        actions.setError(message);
        actions.setBusy(false);
      }
    }
  ),

  fundCodes: null,
  setFundCodes: action((state, payload) => {
    state.fundCodes = payload;
  }),
  funds: null,
  setFunds: action((state, payload) => {
    state.funds = payload;
  }),
  getFundCodes: thunk((actions, _void, helpers) => {
    return runApi(
      actions,
      helpers,
      async () => {
        const res = await helpers.injections.apiClient.listAssets({
          ...defaultListAssetsRequest,
          types: [API.MarketAssetType.Fund],
        });

        const codes = (res.result ?? []).flatMap(item =>
          typeof item.code === 'string' ? [item.code] : []
        );

        return {
          ...res,
          result: codes,
        };
      },
      result => {
        actions.setFundCodes(result);
      }
    );
  }),
  getFundDetails: thunk((actions, codes, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.listAssetDetails({
          assets: codes,
          pageSize: codes.length,
          page: 1,
        });
      },
      result => {
        actions.setFunds(result);
      }
    );
  }),
  getFunds: thunk(async actions => {
    const { result: codes } = await actions.getFundCodes();

    if (codes) {
      const { result } = await actions.getFundDetails(codes);

      return result ?? [];
    }

    return [];
  }),
  startAggregatorTracking: thunk(
    (
      actions,
      _payload,
      { injections: { marketsDataAggregator }, getState }
    ) => {
      trackMarketsData(
        marketsDataAggregator,
        () => {
          const _state = getState();
          if (_state.paused) {
            return true;
          }
          if (!_state._data) {
            return true;
          }
          return false;
        },
        () => {
          return getState()._data?.result;
        },
        data => {
          const _state = getState();
          if (!_state._data) {
            return;
          }
          const _newData: MarketData = {
            result: data,
            pagination: _state._data.pagination,
          };
          actions._setData(_newData);
        }
      );
    }
  ),
};
